<script setup lang="ts">
  import { onMounted, watch } from 'vue'

  import { useRoute, useRouter } from 'nuxt/app'

  import { useQueryClient } from '@tanstack/vue-query'

  import SearchBar from './SearchBar.vue'
  import { useSearch } from '../composables/useSearch'

  const props = defineProps({
    locationId: {
      type: String,
      required: true,
    },
    groupId: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: 'Search posts, comments...',
    },
    size: {
      type: String as () => 'xs' | 'sm' | 'md' | 'lg',
      default: 'xs',
    },
    /**
     * Use query params mode - updates URL query instead of navigating
     * When true, adds ?query=... to current route
     */
    useQueryParams: {
      type: Boolean,
      default: false,
    },
  })

  const route = useRoute()
  const router = useRouter()
  const search = useSearch()
  const { uiStore, queryStore } = search
  const queryClient = useQueryClient()

  const handleSearch = (query: string) => {
    if (!props.useQueryParams) return

    // Use replace with explicit path to preserve current route (GoKollab or regular)
    // This prevents the route from resolving to its canonical path
    router.replace({
      path: route.path, // Preserve exact current path
      query: {
        ...route.query,
        query: query || undefined, // Remove query param if empty
      },
    })
  }

  const handleClear = () => {
    // Clear UI store first
    uiStore.clearQuery()

    // Clear search cache to remove cached search results
    // This ensures search results don't persist after clearing
    queryClient.removeQueries({
      queryKey: ['search', props.locationId, props.groupId],
    })

    if (!props.useQueryParams) {
      return
    }

    // Remove query param from URL while preserving exact current path
    const newQuery = { ...route.query }
    delete newQuery.query

    // Use replace with explicit path to preserve current route (GoKollab or regular)
    router.replace({
      path: route.path, // Preserve exact current path
      query: newQuery,
    })
  }

  onMounted(() => {
    if (props.useQueryParams && route.query.query) {
      uiStore.setQuery(route.query.query as string)
    } else if (props.useQueryParams && !route.query.query) {
      // Ensure UI store is cleared if no query in URL
      uiStore.clearQuery()

      // Clear search cache on mount if no query
      // This handles direct navigation to page without search query
      queryClient.removeQueries({
        queryKey: ['search', props.locationId, props.groupId],
      })
    }
  })

  // Watch for route query changes (e.g., navigation between tabs)
  watch(
    () => route.query.query,
    newQuery => {
      if (!props.useQueryParams) return

      const queryString = (newQuery as string) || ''

      // Only update if different from current UI store value
      if (queryString !== uiStore.query) {
        if (queryString) {
          uiStore.setQuery(queryString)
        } else {
          // Clear UI store
          uiStore.clearQuery()

          // Clear search cache when query becomes empty
          // This handles navigation between tabs where query param is removed
          queryClient.removeQueries({
            queryKey: ['search', props.locationId, props.groupId],
          })
        }
      }
    },
    { immediate: true }
  )
</script>

<template>
  <div id="search-widget-container" class="search-widget w-[25vw]">
    <SearchBar :placeholder="placeholder" :size="size" @search="handleSearch" @clear="handleClear" />
  </div>
</template>
